import React from 'react';
import { loadCalendarMonth } from '../utils/hinduCalendarCache';
import { Box, Card, CardContent, Chip, CircularProgress, Container, Divider, Stack } from '@mui/material';
import { ArrowBack, CalendarMonth, LocationOn } from '@mui/icons-material';
import HinduCalendarSeo from '../components/seo/HinduCalendarSeo';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PanchangAlmanac from '../components/panchang/PanchangAlmanac';
import Typography from '../components/i18n/LocalizedTypography';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip } from '../components/i18n/LocalizedMui';
import { inlineLocale } from '../i18n/inlineTranslations';
import { hinduCalendarDateLabel, hinduCalendarFestivalName, hinduCalendarFestivalType, hinduCalendarText } from '../i18n/hinduCalendarTranslations';
import { festivalsForCalendarDay, hinduCalendarFestivalKey } from './HinduCalendar';
import type { HinduCalendarDayData, HinduCalendarFestival } from './HinduCalendar';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const ICONS: Record<string, string> = {
  new_moon: '🌑', full_moon: '🌕', vrat: '🙏', sun: '☀️', diya: '🪔',
  shiva: '🔱', ganesha: '🐘', flag: '🇮🇳', kalash: '🪷',
};

const numericParam = (params: URLSearchParams, name: string, fallback: number) => {
  const rawValue = params.get(name);
  if (rawValue === null || rawValue.trim() === '') return fallback;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : fallback;
};

const formatTime = (value: string | undefined, locale: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(parsed);
};

const humanize = (value?: string) => String(value || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const isValidIsoDate = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return parsed.getFullYear() === Number(match[1]) && parsed.getMonth() === Number(match[2]) - 1 && parsed.getDate() === Number(match[3]);
};

const mergeCalendarDay = (fresh: HinduCalendarDayData, selected?: HinduCalendarDayData | null): HinduCalendarDayData => {
  if (!selected || selected.date !== fresh.date) return fresh;
  const festivals = new Map<string, HinduCalendarFestival>();
  for (const festival of [...festivalsForCalendarDay(selected), ...festivalsForCalendarDay(fresh)]) {
    const key = `${festival.id}:${festival.timing?.start || ''}:${festival.name}`;
    festivals.set(key, festival);
  }
  return { ...fresh, festivals: Array.from(festivals.values()) };
};

const HinduCalendarDay: React.FC = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { date = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || 'en').split(/[-_]/)[0];
  const locale = inlineLocale(language);
  const copy = React.useCallback((key: Parameters<typeof hinduCalendarText>[1]) => hinduCalendarText(language, key), [language]);
  const selectedDay = (routeLocation.state as { calendarDay?: HinduCalendarDayData } | null)?.calendarDay || null;
  const [day, setDay] = React.useState<HinduCalendarDayData | null>(() => selectedDay?.date === date ? selectedDay : null);
  const [loading, setLoading] = React.useState(() => selectedDay?.date !== date);
  const [error, setError] = React.useState<string | null>(null);

  const validDate = isValidIsoDate(date);
  const latitude = numericParam(searchParams, 'lat', 28.6139);
  const longitude = numericParam(searchParams, 'lon', 77.209);
  const timezone = searchParams.get('timezone') || 'Asia/Kolkata';
  const timezoneOffset = numericParam(searchParams, 'timezoneOffset', 330);
  const locationLabel = searchParams.get('location') || 'New Delhi, India';

  React.useEffect(() => {
    if (!validDate) {
      setError(copy('invalidDate'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    const parsed = new Date(`${date}T12:00:00`);
    setLoading(true);
    setError(null);
    loadCalendarMonth(`${API_BASE_URL}/hindu-calendar/month`, { year: parsed.getFullYear(), month: parsed.getMonth() + 1, lat: latitude, lon: longitude, timezone, timezoneOffset
    }).then((response) => {
      if (cancelled) return;
      const match = (response.data?.days || []).find((item: HinduCalendarDayData) => item.date === date);
      if (!match) throw new Error(copy('missingDate'));
      setDay(mergeCalendarDay(match, selectedDay));
    }).catch((requestError) => {
      if (!cancelled) setError(requestError.response?.data?.error || requestError.message || copy('loadDayError'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [copy, date, latitude, longitude, selectedDay, timezone, timezoneOffset, validDate]);

  const backToCalendar = () => {
    const query = new URLSearchParams({
      year: searchParams.get('viewYear') || date.slice(0, 4),
      month: searchParams.get('viewMonth') || String(Number(date.slice(5, 7))),
      selectedDate: searchParams.get('selectedDate') || date,
      lat: String(latitude), lon: String(longitude), timezone, timezoneOffset: String(timezoneOffset),
      location: locationLabel,
    });
    navigate(`/hindu-calendar?${query.toString()}`);
  };

  const festivalName = (festival: HinduCalendarFestival) => hinduCalendarFestivalName(language, festival);
  const formattedDate = validDate ? hinduCalendarDateLabel(language, new Date(`${date}T12:00:00`), locale, true) : date;
  const normalizedPanchang = day ? {
    ...day.panchang,
    date: day.date,
    sunrise: formatTime(day.panchang?.sunrise, locale), sunset: formatTime(day.panchang?.sunset, locale),
    moonrise: formatTime(day.panchang?.moonrise, locale), moonset: formatTime(day.panchang?.moonset, locale),
  } : null;

  return (
    <>
      <HinduCalendarSeo date={date} validDate={validDate} />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Stack spacing={3}>
          <Box>
            <Button startIcon={<ArrowBack />} onClick={backToCalendar} sx={{ mb: 2 }}>{copy('back')}</Button>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }}>
              <CalendarMonth color="primary" sx={{ fontSize: 44 }} />
              <Box>
                <Typography component="h1" variant="h4" fontWeight={900}>{formattedDate}</Typography>
                <Typography color="text.secondary"><LocationOn fontSize="inherit" /> {locationLabel}</Typography>
              </Box>
            </Stack>
          </Box>

          {loading && <Box py={10} textAlign="center"><CircularProgress /><Typography mt={2}>{copy('loadingDay')}</Typography></Box>}
          {error && <Alert severity="error">{error}</Alert>}

          {day && <>
            <Card sx={{ borderColor: day.hasMajorFestival ? 'warning.main' : 'primary.main' }}>
              <CardContent>
                <Typography variant="h5" fontWeight={900}>{copy('festivalDetails')}</Typography>
                <Divider sx={{ my: 2 }} />
                {festivalsForCalendarDay(day).length === 0 ? <Typography color="text.secondary">{copy('emptyDay')}</Typography> : (
                  <Stack spacing={1.5}>
                    {festivalsForCalendarDay(day).map((festival) => (
                      <Box key={hinduCalendarFestivalKey(festival, day.date)} sx={{ p: 2, borderRadius: 2, bgcolor: festival.isMajor ? 'rgba(245, 158, 11, 0.12)' : 'action.hover' }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontSize={28}>{ICONS[festival.icon] || ICONS.kalash}</Typography>
                          <Typography variant="h6" fontWeight={900}>{festivalName(festival)}</Typography>
                          {festival.isMajor && <LocalizedChip size="small" color="warning" label={copy('major')} />}
                          {festival.type && <Chip size="small" variant="outlined" label={hinduCalendarFestivalType(language, festival.type)} />}
                        </Stack>
                        <Stack spacing={0.5} mt={1}>
                          <Typography variant="body2"><strong>{copy('date')}:</strong> {hinduCalendarDateLabel(language, new Date(`${festival.dateStart}T12:00:00`), locale, true)}</Typography>
                          {festival.spansDates && <Typography variant="body2"><strong>{copy('observedSpan')}:</strong> {festival.dateStart} – {festival.dateEnd}</Typography>}
                          {festival.timing && <Typography variant="body2" color="text.secondary"><strong>{festival.timing.type === 'parana' ? copy('parana') : festival.timing.type === 'exact' ? copy('exactTime') : copy('festivalTiming')}:</strong>{' '}{formatTime(festival.timing.start, locale)}{festival.timing.end !== festival.timing.start ? ` – ${formatTime(festival.timing.end, locale)}` : ''}</Typography>}
                          {!!festival.regions?.length && festival.regions[0] !== 'ALL' && <Typography variant="body2"><strong>{copy('regions')}:</strong> {festival.regions.join(', ')}</Typography>}
                          {!!festival.tags?.length && <Typography variant="body2"><strong>{copy('tags')}:</strong> {festival.tags.map(humanize).join(', ')}</Typography>}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Box>
              <Typography variant="h5" fontWeight={900} mb={1.5}>{copy('completePanchang')}</Typography>
              {normalizedPanchang && <PanchangAlmanac panchang={normalizedPanchang} locationLabel={locationLabel} />}
            </Box>
          </>}
        </Stack>
      </Container>
    </>
  );
};

export default HinduCalendarDay;
